import React, { useCallback, useMemo, useState } from 'react';
import { Bot, Download, Home, PlusCircle, Share2, Sparkles, Wrench, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import KnowledgeCategoryHome from '@/components/knowledge/KnowledgeCategoryHome';
import DocumentsList from '@/components/knowledge/DocumentsList';
import KnowledgeBaseHeader from '@/components/knowledge/KnowledgeBaseHeader';
import VaultBrowser from '@/components/knowledge/VaultBrowser';
import LibrarianActivityList from '@/components/knowledge/LibrarianActivityList';
import KnowledgeGraphView from '@/components/knowledge/KnowledgeGraphView';
import type {
  KnowledgeBase,
  KnowledgeBaseCategory,
  KnowledgeBaseDocument,
  KnowledgeBaseVaultLink,
  KnowledgeBaseVaultPage,
  KnowledgeBaseVaultRun,
} from '@/components/knowledge/types';
import { cn } from '@/lib/utils';

interface KnowledgeBaseContainerProps {
  appTitle?: string;
  knowledgeBases: KnowledgeBase[];
  categories: KnowledgeBaseCategory[];
  assignmentByKnowledgeBaseId: Record<string, string>;
  openKnowledgeBaseIds: string[];
  activeKnowledgeBaseId: string | null;
  loading: boolean;
  categoriesLoading?: boolean;
  activeKnowledgeBase: KnowledgeBase | null;
  onOpenKnowledgeBase: (kb: KnowledgeBase) => void;
  onActivateKnowledgeBaseTab: (knowledgeBaseId: string | null) => void;
  onCloseKnowledgeBase: (knowledgeBaseId: string) => void;
  onCreateKnowledgeBase: () => void;
  onCreateKnowledgeBaseInCategory: (categoryId: string | null, categoryLabel: string) => void;
  onEditKnowledgeBase: (kb: KnowledgeBase) => void;
  onRequestDeleteKnowledgeBase: (kb: KnowledgeBase) => void;
  onRequestDeleteSelectedKnowledgeBases: (ids: string[]) => void;
  onCreateCategory: (name: string) => Promise<void>;
  onMoveKnowledgeBasesToCategory: (knowledgeBaseIds: string[], categoryId: string | null) => Promise<boolean>;
  onAddDocument: () => void;
  onShare: () => void;
  onEditDocument: (doc: KnowledgeBaseDocument) => void;
  onDeleteDocument: (doc: KnowledgeBaseDocument) => void;
  onProcessDocument: (doc: KnowledgeBaseDocument) => void;
  onOpenIngestDialog: () => void;
  onOpenLintDialog: () => void;
  onOpenExportDialog: () => void;
  onInitializeVault: () => void;
  vaultPages: KnowledgeBaseVaultPage[];
  vaultLinks: KnowledgeBaseVaultLink[];
  vaultRuns: KnowledgeBaseVaultRun[];
  vaultLoading: boolean;
  hasVault: boolean;
  activeWorkspaceTab: 'sources' | 'vault' | 'graph' | 'activity';
  onActiveWorkspaceTabChange: (tab: 'sources' | 'vault' | 'graph' | 'activity') => void;
  graphRefreshKey?: string | number;
  fullscreenHostRef?: React.RefObject<HTMLDivElement | null>;
}

const KnowledgeBaseContainer = ({
  appTitle = 'Knowledge Base',
  knowledgeBases,
  categories,
  assignmentByKnowledgeBaseId,
  openKnowledgeBaseIds,
  activeKnowledgeBaseId,
  loading,
  categoriesLoading = false,
  activeKnowledgeBase,
  onOpenKnowledgeBase,
  onActivateKnowledgeBaseTab,
  onCloseKnowledgeBase,
  onCreateKnowledgeBase,
  onCreateKnowledgeBaseInCategory,
  onEditKnowledgeBase,
  onRequestDeleteKnowledgeBase,
  onRequestDeleteSelectedKnowledgeBases,
  onCreateCategory,
  onMoveKnowledgeBasesToCategory,
  onAddDocument,
  onShare,
  onEditDocument,
  onDeleteDocument,
  onProcessDocument,
  onOpenIngestDialog,
  onOpenLintDialog,
  onOpenExportDialog,
  onInitializeVault,
  vaultPages,
  vaultLinks,
  vaultRuns,
  vaultLoading,
  hasVault,
  activeWorkspaceTab,
  onActiveWorkspaceTabChange,
  graphRefreshKey,
  fullscreenHostRef,
}: KnowledgeBaseContainerProps) => {
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
  const [selectedVaultPageId, setSelectedVaultPageId] = useState<string | null>(null);

  const openKnowledgeBases = useMemo(
    () => openKnowledgeBaseIds
      .map((knowledgeBaseId) => knowledgeBases.find((knowledgeBase) => knowledgeBase.id === knowledgeBaseId))
      .filter((knowledgeBase): knowledgeBase is KnowledgeBase => Boolean(knowledgeBase)),
    [knowledgeBases, openKnowledgeBaseIds]
  );

  const openKnowledgeBaseFromHome = (knowledgeBase: KnowledgeBase) => {
    onOpenKnowledgeBase(knowledgeBase);
    onActivateKnowledgeBaseTab(knowledgeBase.id);
  };

  const handleOpenVaultPageFromGraph = useCallback((target: { pageId?: string | null; path?: string | null }) => {
    const targetPath = target.path?.trim() ?? null;
    const resolvedPageId = target.pageId
      ?? (targetPath
        ? (
          vaultPages.find((page) => page.path === targetPath)
          ?? vaultPages.find((page) => `${page.slug}.md` === targetPath || page.slug === targetPath.replace(/\.md$/i, ''))
        )?.id
        : null)
      ?? null;

    if (!resolvedPageId) {
      return;
    }

    setSelectedVaultPageId(resolvedPageId);
    onActiveWorkspaceTabChange('vault');
  }, [onActiveWorkspaceTabChange, vaultPages]);

  return (
    <Tabs value={activeKnowledgeBaseId ?? 'home'} className="flex h-full min-h-0 flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 overflow-x-auto">
          <div className="inline-flex min-w-max items-center gap-2 rounded-xl border border-border/70 bg-card/70 p-1">
            <button
              type="button"
              className={cn(
                'inline-flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                !activeKnowledgeBaseId
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              onClick={() => onActivateKnowledgeBaseTab(null)}
            >
              <Home className="mr-2 h-4 w-4" />
              Home
            </button>

            {openKnowledgeBases.map((knowledgeBase) => {
              const isActive = activeKnowledgeBaseId === knowledgeBase.id;

              return (
                <div
                  key={knowledgeBase.id}
                  className={cn(
                    'group flex items-center rounded-lg border transition-colors',
                    isActive
                      ? 'border-border bg-background text-foreground shadow-sm'
                      : 'border-transparent text-muted-foreground hover:border-border/60 hover:text-foreground'
                  )}
                >
                  <button
                    type="button"
                    className="max-w-[220px] truncate px-3 py-2 text-sm font-medium"
                    onClick={() => onActivateKnowledgeBaseTab(knowledgeBase.id)}
                  >
                    {knowledgeBase.name}
                  </button>
                  <button
                    type="button"
                    className="mr-1 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    onClick={(event) => {
                      event.stopPropagation();
                      onCloseKnowledgeBase(knowledgeBase.id);
                    }}
                    aria-label={`Close ${knowledgeBase.name}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <Button onClick={onCreateKnowledgeBase} className="shrink-0">
          <PlusCircle className="mr-2 h-4 w-4" />
          New
        </Button>
      </div>

      <TabsContent value="home" className="mt-0 min-h-0 flex-1 overflow-y-auto">
        <KnowledgeCategoryHome
          knowledgeBases={knowledgeBases}
          categories={categories}
          assignmentByKnowledgeBaseId={assignmentByKnowledgeBaseId}
          loading={loading || categoriesLoading}
          onOpenKnowledgeBase={openKnowledgeBaseFromHome}
          onCreateKnowledgeBaseInCategory={onCreateKnowledgeBaseInCategory}
          onEditKnowledgeBase={onEditKnowledgeBase}
          onDeleteKnowledgeBase={onRequestDeleteKnowledgeBase}
          onDeleteSelectedKnowledgeBases={onRequestDeleteSelectedKnowledgeBases}
          onCreateCategory={onCreateCategory}
          onMoveKnowledgeBasesToCategory={onMoveKnowledgeBasesToCategory}
        />
      </TabsContent>

      {openKnowledgeBases.map((knowledgeBase) => (
        <TabsContent key={knowledgeBase.id} value={knowledgeBase.id} className="mt-0 min-h-0 flex-1">
          {activeKnowledgeBase?.id === knowledgeBase.id ? (
            <div className="flex h-full min-h-0 flex-col gap-4">
              <KnowledgeBaseHeader
                name={activeKnowledgeBase.name}
                description={activeKnowledgeBase.description || 'Knowledge bases now support raw sources, a Librarian-maintained markdown vault, and a graph-native knowledge layer.'}
                collapsed={isHeaderCollapsed}
                onToggleCollapse={() => setIsHeaderCollapsed((current) => !current)}
                actions={
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={onAddDocument}>
                      <PlusCircle className="mr-2 h-4 w-4" />
                      Add Source
                    </Button>
                    <Button size="sm" variant="outline" onClick={onOpenIngestDialog}>
                      <Bot className="mr-2 h-4 w-4" />
                      Ingest Source
                    </Button>
                    <Button size="sm" variant="outline" onClick={onInitializeVault}>
                      <Sparkles className="mr-2 h-4 w-4" />
                      {hasVault ? 'Open Vault' : 'Enable Librarian'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={onOpenLintDialog}>
                      <Wrench className="mr-2 h-4 w-4" />
                      Run Lint
                    </Button>
                    <Button size="sm" variant="outline" onClick={onOpenExportDialog}>
                      <Download className="mr-2 h-4 w-4" />
                      Export Vault
                    </Button>
                    <Button size="sm" variant="outline" onClick={onShare}>
                      <Share2 className="mr-2 h-4 w-4" />
                      Share
                    </Button>
                  </div>
                }
              />

              <div className={cn('min-h-0 flex-1 pr-1', activeWorkspaceTab === 'graph' || activeWorkspaceTab === 'vault' ? 'overflow-hidden' : 'overflow-y-auto')}>
                <Tabs value={activeWorkspaceTab} onValueChange={(value) => onActiveWorkspaceTabChange(value as 'sources' | 'vault' | 'graph' | 'activity')} className="flex h-full min-h-0 flex-col gap-4">
                  <TabsList className="w-fit">
                    <TabsTrigger value="sources">Sources</TabsTrigger>
                    <TabsTrigger value="vault">Vault</TabsTrigger>
                    <TabsTrigger value="graph">Graph</TabsTrigger>
                    <TabsTrigger value="activity">Activity</TabsTrigger>
                  </TabsList>

                  <TabsContent value="sources" className="mt-0">
                    <DocumentsList
                      documents={activeKnowledgeBase.documents || []}
                      activeDocumentId={null}
                      onEdit={onEditDocument}
                      onDelete={onDeleteDocument}
                      onAddDocument={onAddDocument}
                      onProcessWithLibrarian={onProcessDocument}
                    />
                  </TabsContent>

                  <TabsContent value="vault" className="mt-0 h-full min-h-0">
                    {hasVault || vaultPages.length > 0 ? (
                      <VaultBrowser pages={vaultPages} links={vaultLinks} selectedPageId={selectedVaultPageId} onSelectPageId={setSelectedVaultPageId} />
                    ) : (
                      <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
                        <p className="text-base font-medium text-foreground">No vault yet</p>
                        <p className="mt-2 text-sm">Enable Librarian to bootstrap index, overview, log, and folder pages for this knowledge base.</p>
                        <Button className="mt-4" onClick={onInitializeVault}>
                          <Sparkles className="mr-2 h-4 w-4" />
                          Initialize vault
                        </Button>
                      </div>
                    )}
                    {vaultLoading && <p className="mt-3 text-sm text-muted-foreground">Refreshing vault…</p>}
                  </TabsContent>

                  <TabsContent value="graph" className="mt-0 h-full min-h-0">
                    <KnowledgeGraphView
                      knowledgeBaseId={activeKnowledgeBase.id}
                      refreshKey={graphRefreshKey}
                      fullscreenHostRef={fullscreenHostRef}
                      onOpenVaultPage={handleOpenVaultPageFromGraph}
                    />
                  </TabsContent>

                  <TabsContent value="activity" className="mt-0">
                    <LibrarianActivityList runs={vaultRuns} />
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          ) : (
            <div className="flex h-full min-h-[18rem] items-center justify-center rounded-lg border border-dashed border-border/70 bg-muted/10 p-6">
              <p className="text-muted-foreground">Loading {knowledgeBase.name}…</p>
            </div>
          )}
        </TabsContent>
      ))}
    </Tabs>
  );
};

export default KnowledgeBaseContainer;